import React, { useState, useCallback } from 'react';
import classnames from 'classnames';

import { Chip, Button, Menu, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Icon, Translate } from '@hydra/common/components';
import { useUrlChange } from '@hydra/common/customHooks';
import { removeProtocol } from '@hydra/utils/urlUtil';
import * as fetcher from '@hydra/utils/fetcher';

const useStyles = makeStyles(theme => ({
  elementWidth: {
    width: '752px'
  },
  dropdown: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0, 2)
  },
  url: {
    textTransform: 'lowercase'
  },
  button: {
    width: '100%'
  },
  buttonLabel: {
    justifyContent: 'space-between'
  },
  buttonOutlined: {
    '&:focus, &:hover, &:active': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.grey[400]}`
    }
  },
  menuItem: {
    justifyContent: 'space-between'
  },
  menuItemSelected: {
    background: theme.palette.grey[200]
  }
}));

interface DropdownChangeUrlProps {
  href: string
  siteId: string
  stagingUrl: string
  productionUrl?: string
  disabled: boolean
}

export default function DropdownChangeUrl ({ href, siteId, stagingUrl, productionUrl, disabled = false }: DropdownChangeUrlProps) {
  href = href ? removeProtocol(href) : href;
  stagingUrl = stagingUrl ? removeProtocol(stagingUrl) : stagingUrl;
  productionUrl = productionUrl ? removeProtocol(productionUrl) : productionUrl;

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const openDropdown = () => setShowDropdown(true);
  const closeDropdown = () => setShowDropdown(false);
  const urls = [{ url: stagingUrl, isProduction: false }];
  if (productionUrl) {
    urls.push({ url: productionUrl, isProduction: true });
  }

  const urlToGenerate = href === stagingUrl && productionUrl ? productionUrl : stagingUrl;
  const refreshReview = useUrlChange({ url: urlToGenerate });

  function handleMenuItemClick (url: any) {
    closeDropdown();
    if (href !== url) {
      saveDefaultEnv(urlToGenerate === stagingUrl ? 'staging' : 'production');
      refreshReview();
    }
  }
  function handleDropdownClick (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setAnchorEl(e.currentTarget);
    openDropdown();
  }

  const saveDefaultEnv = useCallback(async (env) => {
    try {
      await fetcher.post(`/api/webflow/sites/${siteId}/default_env`, { json: { env } }).json();
    } catch (error) {

    }
  }, [siteId]);

  return (
    <>
      <Button
        disabled={disabled}
        disableElevation
        variant='outlined'
        onClick={handleDropdownClick}
        endIcon={<Icon {...disabled ? { color: 'grey-light' } : {}} type='chevron-down' />}
        className={classnames(classes.dropdown, classes.button, classes.elementWidth)}
        classes={{
          outlined: classes.buttonOutlined,
          label: classes.buttonLabel
        }}
      >
        <Typography noWrap className={classes.url}>
          {href}
        </Typography>
      </Button>
      <Menu
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        open={showDropdown}
        onClose={closeDropdown}
        classes={{
          paper: classes.elementWidth
        }}
      >
        {urls.map((url) => (
          <MenuItem
            key={url.url}
            onClick={() => handleMenuItemClick(url.url)}
            className={classnames(classes.menuItem, url.url === href && classes.menuItemSelected)}
          >
            <Typography noWrap>
              {url.url}
            </Typography>
            {url.isProduction && (
              <Chip
                variant='outlined'
                size='small'
                color='primary'
                label={<Translate content='chip_text_production' />}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
